import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { ColDef, ColGroupDef, RowClassRules } from 'ag-grid-community';
import { CalculationsService } from '../../../../../services/calculations/calculations.service';
import { Subscription } from 'rxjs';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { AgNoRowsOverlayComponent } from '../../../../ag-grid-components/ag-no-rows-overlay/ag-no-rows-overlay.component';
import { DxsrExcelStyling } from '../dxsr-excel-styling';
import { AgGridFunctions } from '../../../../../helpers/ag-grid-functions';
import { OpptyHccValuationReportItem } from '../../../../../models/oppty-hcc-valuation-report-item';
import { AgLoadingOverlayComponent } from '../../../../ag-grid-components/ag-loading-overlay/ag-loading-overlay.component';
import { AgGridExportHelper, IQueryFilterRow } from '../../../../../helpers/ag-grid-export-helper';
import { ClientToastController } from '../../../../../toasts/client-toast.lib';

@Component({
    selector: 'app-oppty-hcc-valuation-report',
    templateUrl: './oppty-hcc-valuation-report.component.html',
    styleUrls: ['./oppty-hcc-valuation-report.component.scss'],
    imports: [AgGridModule]
})
export class OpptyHccValuationReportComponent implements OnInit, OnDestroy {
  private readonly exportErrorToastDurationMs = 5000;
  data: OpptyHccValuationReportItem[] = [];

  columnDefs: (ColDef | ColGroupDef)[] = [];

  defaultColDef: ColDef = {
    menuTabs: [],
    minWidth: 150,
    maxWidth: 150,
    autoHeight: true
  };

  noRowsOverlayComponent: any = AgNoRowsOverlayComponent;
  noRowsOverlayComponentParams: any = {
    noRowsMessageFunc: () => 'No results returned',
  };

  loadingOverlayComponent: any = AgLoadingOverlayComponent;

  public rowClassRules: RowClassRules = {
    // row style function
    'parent-cell': (params) => {
      return params.data.DisplayName === this.calculationsService.hccValuationClosedRowName
      || params.data.DisplayName === this.calculationsService.hccValuationOpenRowName
      || params.data.DisplayName === this.calculationsService.hccValuationTotalRowName;
    },
  };

  excelStyles = DxsrExcelStyling.excelStyles2;

  gridContext: any;

  payers = ["Grand Total All Payers"];
  gridReady = false;

  dxSwitch = false;

  currentYear = 0;

  currentRowHeight = 140;
  currentGridWidth = 282;

  totalSectionName = "Grand Total All Payers";

  subs: Subscription[] = [];
  private isDestroyed = false;
  private agGridExportHelper: AgGridExportHelper;

  @Output() ready: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() setExportEnable: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChildren(AgGridAngular) grids!: QueryList<AgGridAngular>;

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.setGridColumnsToFit();
  }

  constructor(
    private calculationsService: CalculationsService,
    private toastController: ClientToastController) {
    this.agGridExportHelper = new AgGridExportHelper();
    this.gridContext = { componentParent: this };
  }

  ngOnInit(): void {
    //Data sub
    this.subs.push(this.calculationsService.hccValuation.subscribe(result => {
      this.data = result || [];
      this.payers = this.getPayersFromData(this.data);

      // Allow @for to render payer-specific grids before setting each grid's data.
      setTimeout(() => {
        // The grids may have been destroyed by navigating away before this runs.
        if (this.isDestroyed) {
          return;
        }

        this.updateGridData();
        this.setGridColumnsToFit();
        this.currentRowHeight = 64 + ((this.data.length + 3) * 29);
        this.currentGridWidth = 752;
        this.hideGridsLoadingOverlay();
        this.setExportEnable.emit(true);
      }, 0);
    }));
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.subs.forEach(s => s.unsubscribe());
  }

  updateServiceYear(serviceYear: number) {
    this.currentYear = serviceYear;
    this.calculationsService.getOpptyPreferredActionHccValuation(serviceYear);
    this.showGridsLoadingOverlay();
  }

  private normalizePayerName(name?: string): string | undefined {
    const normalizedName = name?.trim();
    return normalizedName ? normalizedName : undefined;
  }

  private getPayersFromData(items: OpptyHccValuationReportItem[]): string[] {
    const payerNames = Array.from(new Set(
      items
        .map(i => this.normalizePayerName(i.PayerName))
        .filter((name): name is string => !!name)
    ));

    return [this.totalSectionName, ...payerNames];
  }

  getColDef(sectionName: string): (ColDef | ColGroupDef)[] {

    const result: any = [
      {
        headerName: sectionName,
        headerTooltip: sectionName,
        children: [
          { field: 'DisplayName', headerName: '',
            minWidth: 250,
            maxWidth: 600,
            cellClassRules: {
              'dxsr-display-name-1': (params) => {
                return params.data.DisplayName === this.calculationsService.hccValuationClosedRowName
                || params.data.DisplayName === this.calculationsService.hccValuationOpenRowName
                || params.data.DisplayName === this.calculationsService.hccValuationTotalRowName
                || params.data.DisplayName === this.calculationsService.hccValuationPendingRowName;
              },
              'dxsr-display-name-4': (params) => {
                return  params.data.Parent != null && params.data.Parent != "";
              },
            }
          },
          { field: "TotalCount", headerName: 'DX Event Count', type: 'numericColumn',
            valueFormatter: AgGridFunctions.getNumberValue,
            cellClassRules: {
              'ag-parent-cell-left-border': (params) => {
                return params.data.DisplayName === this.calculationsService.hccValuationClosedRowName
                || params.data.DisplayName === this.calculationsService.hccValuationOpenRowName
                || params.data.DisplayName === this.calculationsService.hccValuationTotalRowName
                || params.data.DisplayName === this.calculationsService.hccValuationPendingRowName;
              },
              'ag-cell-left-border': (params) => {
                return params.data.Parent != null && params.data.Parent != "";
              }
            },
            cellClass: 'numberType'
          },
          { field: "HCCCount", headerName: 'Distinct HCC Count', type: 'numericColumn',
            valueFormatter: AgGridFunctions.getNumberValue,
            cellClassRules: {
              'ag-parent-cell-right-border': (params) => {
                return params.data.DisplayName === this.calculationsService.hccValuationClosedRowName
                || params.data.DisplayName === this.calculationsService.hccValuationOpenRowName
                || params.data.DisplayName === this.calculationsService.hccValuationTotalRowName
                || params.data.DisplayName === this.calculationsService.hccValuationPendingRowName;
              },
              'ag-cell-right-border': (params) => {
                return params.data.Parent != null && params.data.Parent != "";
              }
            },
            cellClass: 'numberType'
          },
          { field: "Valuation", headerName: 'HCC Est. $ Valuation', type: 'numericColumn',
            valueFormatter: AgGridFunctions.getCurrencyValue,
            width: 150,
            cellClassRules: {
              'ag-parent-cell-right-border': (params) => {
                return params.data.DisplayName === this.calculationsService.hccValuationClosedRowName
                || params.data.DisplayName === this.calculationsService.hccValuationOpenRowName
                || params.data.DisplayName === this.calculationsService.hccValuationTotalRowName
                || params.data.DisplayName === this.calculationsService.hccValuationPendingRowName;
              },
              'ag-cell-right-border': (params) => {
                return params.data.Parent != null && params.data.Parent != "";
              },
              'currency': () => {return true}
            }
          },
        ]
      }
    ];

    return result;
  }

  updateGridData() {
    this.grids?.forEach(g => {
      const gridName = g?.api?.getGridId();
      const items = gridName == this.totalSectionName
        ? this.data
        : this.data.filter(d => this.normalizePayerName(d.PayerName) == gridName);
      const gridItems = gridName == this.totalSectionName ? this.calculationsService.getHccValuationGrandTotalItems(items) : this.calculationsService.getHccValuationRowItems(items || []);
      g?.api?.setGridOption('rowData', gridItems);
      g?.api?.setGridOption('columnDefs', this.getColDef(gridName));
    });
  }

  setGridColumnsToFit() {
    if(!document.getElementById(this.totalSectionName)) {
      return;
    }

    this.grids?.forEach(g => g?.api?.sizeColumnsToFit());
  }

  showGridsLoadingOverlay() {
    this.grids?.forEach(g => g?.api?.setGridOption('loading', true));
  }

  hideGridsLoadingOverlay() {
    this.grids?.forEach(g => g?.api?.setGridOption('loading', false));
  }

  onExport() {
    try {
      const filename = `HCCEstimatedValuationByPayerExport_${this.currentYear}`;
      this.agGridExportHelper.onExport(
        filename,
        undefined,
        this.getQueryFilters(),
        this.grids,
        [],
        (_grid, index) => index === 0
          ? 'HCC Estimated Valuation by Payer'
          : `${this.payers[index]} Valuation`
      );
    } catch (error) {
      this.toastController.displayToast(
        this.getExportErrorMessage(error),
        this.exportErrorToastDurationMs
      );
    }
  }

  private getQueryFilters(): IQueryFilterRow[] {
    if (!this.currentYear) {
      return [];
    }

    return [
      {
        label: 'Service Year',
        value: this.currentYear.toString(),
        type: 'String'
      }
    ];
  }

  private getExportErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message.trim().length > 0) {
      return `Error: ${error.message}`;
    }

    return 'Error: Unable to export the HCC Estimated Valuation by Payer report.';
  }

}
